import { format as formatDate } from 'date-fns';
import { nl } from 'date-fns/locale';

/**
 * Nederlandse formatters. Alle output gebruikt gewone spaties (geen NBSP),
 * zodat waarden voorspelbaar zijn in tests en in snapshots.
 */
function normalizeSpaces(value: string): string {
    return value.replace(/\u00a0/g, ' ');
}

/**
 * `1450` -> `€ 1.450`, `1450.5` -> `€ 1.450,50`.
 * Hele bedragen worden zonder centen getoond; dat is de afspraak uit het ontwerp.
 */
export function formatCurrency(amount: number, options: { cents?: boolean } = {}): string {
    const showCents = options.cents ?? !Number.isInteger(amount);

    return normalizeSpaces(
        new Intl.NumberFormat('nl-NL', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: showCents ? 2 : 0,
            maximumFractionDigits: showCents ? 2 : 0,
        }).format(amount),
    );
}

/**
 * Bedragen komen uit de API in hele centen.
 */
export function formatCurrencyFromCents(cents: number, options?: { cents?: boolean }): string {
    return formatCurrency(cents / 100, options);
}

export function formatNumber(value: number, fractionDigits = 0): string {
    return normalizeSpaces(
        new Intl.NumberFormat('nl-NL', {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        }).format(value),
    );
}

/** `142` -> `142 kWh/m²·jr` */
export function formatEnergyIndex(value: number, fractionDigits = 0): string {
    return `${formatNumber(value, fractionDigits)} kWh/m²·jr`;
}

/** `2026-09-03T09:00` -> `3 sep 09:00` */
export function formatDayTimeShort(value: Date | string | number): string {
    return stripMonthDot(formatDate(toDate(value), 'd MMM HH:mm', { locale: nl }));
}

/** `2026-09-03` -> `3 sep` */
export function formatDayShort(value: Date | string | number): string {
    return stripMonthDot(formatDate(toDate(value), 'd MMM', { locale: nl }));
}

/** `2026-09-03` -> `3 september 2026` */
export function formatDayLong(value: Date | string | number): string {
    return formatDate(toDate(value), 'd MMMM yyyy', { locale: nl });
}

/** `09:00` */
export function formatTime(value: Date | string | number): string {
    return formatDate(toDate(value), 'HH:mm', { locale: nl });
}

export function toDate(value: Date | string | number): Date {
    return value instanceof Date ? value : new Date(value);
}

function stripMonthDot(value: string): string {
    return value.replace(/\.(?=\s|$)/g, '');
}
